// SPA client for coworker profile edit (Admin coworker edit UI — Task 4).
// Mirrors the shape/style of apps/admin/src/api/accessReaders.ts: every
// function takes the Auth0 access `token` explicitly, threaded last.
//
// The face-photo functions here are thin wrappers around the existing
// apps/admin/src/api/facePhotos.ts client — same `/api/coworkers/:id/face-photo`
// endpoint contract — re-exported under the `*CoworkerFacePhoto` names so
// CoworkerEditForm (and its callers) only need to import from this one
// module.
import type { Coworker } from '@hub-wifi/shared'
import { apiRequest } from './client'
import {
  deleteFacePhoto,
  getFacePhoto,
  uploadFacePhoto,
  type UploadFacePhotoResult,
} from './facePhotos'

/** PATCH body for `/api/coworkers/:id` — mirrors
 * apps/api/src/validators/coworkerSchema.ts#updateCoworkerProfileSchema.
 * Every field is optional (PATCH semantics). */
export interface UpdateCoworkerProfileInput {
  name?: string
  email?: string
  mobilePhone?: string | null
  cpf?: string | null
  taxIdCountry?: string | null
  taxIdType?: string | null
  taxIdValue?: string | null
  birthDate?: string | null
}

export function getCoworker(id: string, token: string): Promise<Coworker> {
  return apiRequest<Coworker>(`/api/coworkers/${id}`, { token })
}

export function updateCoworkerProfile(
  id: string,
  patch: UpdateCoworkerProfileInput,
  token: string,
): Promise<Coworker> {
  return apiRequest<Coworker>(`/api/coworkers/${id}`, {
    method: 'PATCH',
    token,
    body: patch,
  })
}

/** Self-service — `GET /api/coworkers/me` (role 'user'). Returns the caller's
 * own coworker record, resolved server-side via authSub. 404s (surfaced as
 * an `ApiError` with `status === 404`) when the caller has no linked
 * coworker record. */
export function getMyProfile(token: string): Promise<Coworker> {
  return apiRequest<Coworker>('/api/coworkers/me', { token })
}

/** Self-service — `PATCH /api/coworkers/me` (role 'user'). Updates only the
 * caller's own record; the server ignores/rejects any non-profile fields. */
export function updateMyProfile(
  patch: UpdateCoworkerProfileInput,
  token: string,
): Promise<Coworker> {
  return apiRequest<Coworker>('/api/coworkers/me', {
    method: 'PATCH',
    token,
    body: patch,
  })
}

/** Returns the coworker's face photo as a Blob, or null if none is on file (404). */
export function getCoworkerFacePhoto(coworkerId: string, token: string): Promise<Blob | null> {
  return getFacePhoto(coworkerId, token)
}

export function uploadCoworkerFacePhoto(
  coworkerId: string,
  photoBase64: string,
  consent: boolean,
  token: string,
): Promise<UploadFacePhotoResult> {
  return uploadFacePhoto(coworkerId, photoBase64, consent, token)
}

export function deleteCoworkerFacePhoto(
  coworkerId: string,
  token: string,
): Promise<{ ok: boolean }> {
  return deleteFacePhoto(coworkerId, token)
}
